import checkAuthState from './checkAuthState'
import fetchAndSetSiteInfo from './fetchAndSetSiteInfo'
import logout from './logout'
import Auth from '@aws-amplify/auth'
import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken, CognitoUserSession } from 'amazon-cognito-identity-js'

window.AwsAuth = Auth

export default function () {
  const params = {
    region: 'eu-west-1',
    userPoolId: window.siteInfo.cognitoUserPoolId,
    userPoolWebClientId: window.siteInfo.cognitoAppClientId,
    // Required for the migration trigger to work
    authenticationFlowType: 'USER_PASSWORD_AUTH'
  }
  window.AwsAuth.configure(params)

  function CurrentSessionCache (AwsAuth) {
    const prefix = 'CognitoIdentityServiceProvider'
    const cognitoAppClientId = AwsAuth?._config?.userPoolWebClientId ?? ''

    let fetchCache = null
    let currentSessionData = null
    let currentSessionDataPromise = null

    /**
     * @method SessionCache.fetchToken
     */
    this.fetchToken = function fetchToken () {
      if (fetchCache !== null) {
        return fetchCache
      }

      if (!currentSessionData || (currentSessionData.accessToken && (currentSessionData.accessToken.payload.exp * 1000) <= Date.now())) {
        fetchCache = currentSessionDataPromise = AwsAuth.currentSession()
          .then(
            (data) => {
              // refresh successful
              this.currentSessionData = data
              return data
            }).then(() => checkAuthState(true))
          .catch(() => {
            // on fail, wipe out the cache
            this.currentSessionData = null
            return currentSessionData
          }).finally(() => (fetchCache = null))
      }
    }

    Object.defineProperty(this, 'currentSessionData', {
      get: () => {
        this.fetchToken()
        return currentSessionData
      },
      set: (v) => {
        if (!v && currentSessionData) {
          logout()
        }
        currentSessionData = v
      }
    })

    Object.defineProperty(this, 'currentSessionDataPromise', {
      get: () => {
        this.fetchToken()
        return currentSessionDataPromise
      }
    })

    currentSessionData = this.initFromLocalStorage(prefix, cognitoAppClientId)
    currentSessionDataPromise = Promise.resolve(currentSessionData)
    this.attachProperties(AwsAuth)

    const listener = (event) => {
      // matches renewal as well, since the new will trigger the event already
      if (event.key.match(new RegExp(`^${prefix}.*accessToken$`)) === null) {
        return
      }
      window.location.reload()
    }

    window.addEventListener('storage', listener)
    window.onbeforeunload = () => { window.removeEventListener('storage', listener) }
  }

  CurrentSessionCache.prototype.attachProperties = function (AuthObj) {
    if (!Object.prototype.hasOwnProperty.call(AuthObj, 'currentSessionDataPromise')) {
      Object.defineProperty(AuthObj, 'currentSessionData', {
        get: () => this.currentSessionData
      })
    }

    if (!Object.prototype.hasOwnProperty.call(AuthObj, 'currentSessionDataPromise')) {
      Object.defineProperty(AuthObj, 'currentSessionDataPromise', {
        get: () => this.currentSessionDataPromise
      })
    }
  }

  CurrentSessionCache.prototype.initFromLocalStorage = function (prefix, cognitoAppClientId) {
    if (!window.localStorage) {
      return
    }

    const lastAuthUser = window.localStorage.getItem([prefix, cognitoAppClientId, 'LastAuthUser'].join('.'))
    if (!lastAuthUser) {
      return
    }

    const keys = ['accessToken', 'refreshToken', 'idToken', 'clockDrift']
    return new CognitoUserSession(keys.reduce((carry, el) => {
      switch (el) {
        case 'accessToken':
          carry.AccessToken = new CognitoAccessToken({ AccessToken: window.localStorage.getItem([prefix, cognitoAppClientId, lastAuthUser, el].join('.')) })
          break
        case 'idToken':
          carry.IdToken = new CognitoIdToken({ IdToken: window.localStorage.getItem([prefix, cognitoAppClientId, lastAuthUser, el].join('.')) })
          break
        case 'refreshToken':
          carry.RefreshToken = new CognitoRefreshToken({ RefreshToken: window.localStorage.getItem([prefix, cognitoAppClientId, lastAuthUser, el].join('.')) })
          break
        default:
          carry[el] = window.localStorage.getItem([prefix, cognitoAppClientId, lastAuthUser, el].join('.'))
          break
      }
      return carry
    }, {}))
  }

  Object.freeze.call(CurrentSessionCache.prototype)

  const sessionCache = new CurrentSessionCache(window.AwsAuth)

  sessionCache.currentSessionDataPromise
    .then((data) => {
      checkAuthState()
      if (data === null) {
        throw new Error()
      }
    })
    .catch(fetchAndSetSiteInfo) // If no logged in user, call fetchAndSetSiteInfo
}
